import React, { useCallback } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Checkbox from '@mui/material/Checkbox';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 150,
    },
    sx: {
      '& .MuiMenuItem-root': {
        minHeight: 32,
        padding: '4px 16px',
      },
      '& .MuiCheckbox-root': {
        padding: '4px',
      }
    }
  },
};

const MultiSelectFilter = React.memo(({
  label,
  value,
  onChange,
  options,
  minWidth = 140,
}) => {
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    const currentValue = value || [];
    
    // Get all non-"all" option values
    const specificOptions = options.filter(opt => opt.value !== 'all').map(opt => opt.value);
    
    // Determine what was clicked by comparing arrays
    const addedItems = newValue.filter(v => !currentValue.includes(v));
    const removedItems = currentValue.filter(v => !newValue.includes(v));
    
    let next;
    
    // If "all" was just added (clicked)
    if (addedItems.includes('all')) {
      next = ['all'];
    }
    // If a specific option was added while "all" was selected
    else if (currentValue.includes('all') && addedItems.length > 0) {
      // Remove "all" and keep the new specific options
      next = newValue.filter(v => v !== 'all');
    }
    // If "all" was removed (unchecked) and specific options exist
    else if (removedItems.includes('all') && newValue.length > 0) {
      // Keep the specific options
      next = newValue.filter(v => v !== 'all');
    }
    // Normal toggle of specific options
    else {
      // Ensure "all" is not mixed with specific options
      next = newValue.includes('all') ? ['all'] : newValue.filter(v => v !== 'all');
    }
    
    // Check if all specific options are selected - if so, convert to "all"
    const selectedSpecificOptions = next.filter(v => v !== 'all');
    if (selectedSpecificOptions.length > 0 && 
        specificOptions.length > 0 &&
        specificOptions.every(opt => selectedSpecificOptions.includes(opt))) {
      next = ['all'];
    }
    
    // If no options are selected, automatically select "all"
    if (next.length === 0) {
      next = ['all'];
    }
    
    onChange(next);
  }, [value, onChange, options]);

  const renderValue = useCallback((selected) => {
    if (selected.length === 1) {
      const found = options.find(opt => opt.value === selected[0]);
      return found ? found.label : selected[0];
    }
    return 'Multiple';
  }, [options]);

  return (
    <FormControl size="small" sx={{ 
      minWidth,
      '& .MuiInputBase-root': { height: 32 }
    }}>
      <InputLabel id={`${label.toLowerCase().replace(/\s+/g, '-')}-label`}>
        {label}
      </InputLabel>
      <Select
        labelId={`${label.toLowerCase().replace(/\s+/g, '-')}-label`}
        id={`${label.toLowerCase().replace(/\s+/g, '-')}-multiselect`}
        multiple
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={renderValue}
        MenuProps={MenuProps}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox
              size="small"
              checked={value.includes(option.value)}
              sx={{ p: 0, mr: 1 }}
            />
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

MultiSelectFilter.displayName = 'MultiSelectFilter';

export default MultiSelectFilter;

